import React, { useState, useRef, useEffect, useCallback } from "react";
import Modal from "react-modal";
import config from "../configs/config";
import { useAuth } from "../providers/AuthProvider";
import CommonButton from "../elments/CommonButton";
import SingleResponsePreview from "./Survey/SingleResponsePreview";

const SurveyPreview = ({
  sdataGroup: initialSdataGroup,
  surveyName,
  surveyFileName,
  oeParameters,
  onShowToastMessage,
  onSetProcessingMessage,
  questionnaireFileId,
  exportedFiles = {},
  projectId,
  selectedClient, 
  selectedProject,
  onSurveyUpdate,
  powner
}) => {
  const { user } = useAuth();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState("questions");
  const [sdataGroup, setSdataGroup] = useState(initialSdataGroup);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [showSplitView, setShowSplitView] = useState(false);
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedExport, setSelectedExport] = useState(null);

  // AI Assistant States
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [aiSplitRatio, setAiSplitRatio] = useState(50);
  const [isAiDragging, setIsAiDragging] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [questionNumbers, setQuestionNumbers] = useState([]);
  const [targetQuestionKey, setTargetQuestionKey] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allowedDownloadFeatures, setAllowedDownloadFeatures] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
const [pendingChanges, setPendingChanges] = useState({});
const [showCompareModal, setShowCompareModal] = useState(false);
const [compareData, setCompareData] = useState(null);

  const recognitionRef = useRef(null);
  const containerRef = useRef(null);
  const chatEndRef = useRef(null);
  const aiContainerRef = useRef(null);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex >=sdataGroup? sdataGroup?.length - 1:0;

  const handleNext = () => !isLast && setCurrentIndex((prev) => prev + 1);
  const handleBack = () => !isFirst && setCurrentIndex((prev) => prev - 1);

  const currentQuestion =sdataGroup? sdataGroup[currentIndex]:[];

  // useEffect(() => {
  //   // console.log ("DOCX Preview Props:", {
  //   //   questionnaireFileId,
  //   //   surveyFileName,
  //   //   hasDocx: surveyFileName?.toLowerCase().endsWith(".docx"),
  //   // });
  // }, [questionnaireFileId, surveyFileName]);

  useEffect(() => {
    if (aiMessages.length > 0) {
      const messagesToShow = aiMessages.slice(-2);
      setVisibleMessages(messagesToShow);

      const timer = setTimeout(() => {
        setVisibleMessages([]);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [aiMessages]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiMessages]);

  // Initialize Speech Recognition
  useEffect(() => {
    setSdataGroup(initialSdataGroup);
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";
      recognitionRef.current.onstart = () => {
        // console.log ("Listening started");
        setTranscript("Listening...");
      };
      recognitionRef.current.onresult = (event) => {
        if (isSpeaking) {
          // console.log ("Skipping result during speaking");
          return;
        }
        const command = event.results[0][0].transcript.toLowerCase().trim();
        if (command.length < 3) return;
        setTranscript(command);
        // console.log ("Final command:", command);
        handleVoiceCommand(command);
      };
      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        if (event.error !== "aborted" && event.error !== "not-allowed") {
          setTranscript(`Error: ${event.error}`);
          speak("Try again.");
        }
        setIsListening(false);
      };
      recognitionRef.current.onend = () => {
        // console.log ("Listening ended");
        setIsListening(false);
        setTranscript("");
      };
    }
  }, [initialSdataGroup, isSpeaking]);

  // Load DOCX preview when split view is enabled
  useEffect(() => {
    if (showSplitView && !embedUrl && questionnaireFileId) {
      loadDocxPreview();
    }
  }, [showSplitView]);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;

    if (organization) {
      if (
        organization.download_features &&
        Array.isArray(organization.download_features) &&
        organization.download_features.length > 0
      ) {
        // console.log (
        //   "✅ Setting download features:",
        //   organization.download_features
        // );
        setAllowedDownloadFeatures(organization.download_features);
      } else {
        console.warn(
          "⚠️ No download_features found, defaulting to all download features"
        );
        setAllowedDownloadFeatures(["ai_mode"]); // or whatever features you have
      }
    } else {
      console.warn("⚠️ No organization found, allowing all download features");
      setAllowedDownloadFeatures(["ai_mode"]);
    }
  }, [user]);

  const hasDownloadFeatureAccess = (feature) => {
    return allowedDownloadFeatures.includes(feature);
  };

  const showToastMessage = (message) => {
    if (onShowToastMessage) {
      onShowToastMessage(message);
    } else {
      // console.log ("Toast:", message);
      alert(message);
    }
  };

  const setProcessingMessage = (message) => {
    if (onSetProcessingMessage) {
      onSetProcessingMessage(message);
    } else {
      // console.log ("Processing:", message);
    }
  };

  const extractQuestionNumbers = (sdataGroup) => {
    const numbers = [];
    Object.entries(sdataGroup).forEach(([key, question]) => {
      const qNum = question.Question_number || key;
      numbers.push({
        key: key,
        number: qNum,
        displayName: `Q${qNum}`,
        title: question.Qtitle || "Untitled",
      });
    });
    return numbers;
  };

  // Add this function to parse user intent and find target question
  const findTargetQuestion = (userMessage) => {
    const message = userMessage.toLowerCase();

    // console.log ("🔍 Searching in message:", message);
    console.log (
      "📋 Available question keys:",
      questionNumbers.map((q) => q.key)
    );

    // Clean the message to help with pattern matching
    const cleanMessage = message.replace(/\s+/g, " ").trim();

    // 1. FIRST PRIORITY: Look for EXACT question key matches (Q1_1, Q2_3, etc.)
    // Extract all possible question patterns from the message
    const questionPatterns = [];

    // Pattern 1: Q followed by numbers and underscore (Q1_1, q2_3, etc.)
    const qPattern1 = cleanMessage.match(/(?:^|\s)(q\d+_\d+)(?:\s|$)/gi);
    if (qPattern1) questionPatterns.push(...qPattern1.map((p) => p.trim()));

    // Pattern 2: Question followed by numbers and dot/underscore
    const qPattern2 = cleanMessage.match(
      /(?:^|\s)(question\s*\d+[._]\d+)(?:\s|$)/gi
    );
    if (qPattern2) questionPatterns.push(...qPattern2.map((p) => p.trim()));

    // Pattern 3: Just the number with underscore (1_1)
    const qPattern3 = cleanMessage.match(/(?:^|\s)(\d+_\d+)(?:\s|$)/gi);
    if (qPattern3) questionPatterns.push(...qPattern3.map((p) => p.trim()));

    // console.log ("🎯 Potential exact matches found:", questionPatterns);

    // Check each pattern against our question keys
    for (const pattern of questionPatterns) {
      // Normalize the pattern
      let normalizedKey = pattern.toLowerCase();

      // Convert "question 1.1" to "q1_1"
      if (normalizedKey.startsWith("question")) {
        normalizedKey = normalizedKey
          .replace(/question\s*/gi, "q")
          .replace(/[.]/g, "_")
          .replace(/\s+/g, "");
      }
      // Convert "1_1" to "q1_1"
      else if (/^\d+_\d+$/.test(normalizedKey)) {
        normalizedKey = "q" + normalizedKey;
      }

      // Look for exact match in question keys
      for (const q of questionNumbers) {
        const qKey = q.key.toLowerCase();

        if (normalizedKey === qKey) {
          // console.log ("✅ EXACT MATCH FOUND:", q.key);
          return q.key;
        }
      }
    }

    // 2. SECOND PRIORITY: If no exact match, look for contains but ONLY as whole word
    for (const q of questionNumbers) {
      const qKey = q.key.toLowerCase();
      const qNumber = String(q.number).toLowerCase();
      const qDisplay = q.displayName.toLowerCase();

      // Create regex patterns that match whole words only
      const keyRegex = new RegExp(
        `\\b${qKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
        "i"
      );
      const numberRegex = new RegExp(
        `\\b${qNumber.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
        "i"
      );
      const displayRegex = new RegExp(
        `\\b${qDisplay.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
        "i"
      );

      // Check for whole word matches only
      if (
        keyRegex.test(message) ||
        numberRegex.test(message) ||
        displayRegex.test(message)
      ) {
        // console.log ("✅ Whole word match found:", q.key);
        return q.key;
      }
    }

    // 3. THIRD PRIORITY: Fallback - check if message contains question number
    // But ONLY if it's not part of another number
    for (const q of questionNumbers) {
      const qNumber = String(q.number).toLowerCase();

      // Only match standalone numbers (not part of other numbers)
      // For example, "1" should match " q1 " or " 1 " but not "q11" or "consent1"
      const numberRegex = new RegExp(`(?:^|[^\\d])${qNumber}(?:$|[^\\d])`, "i");

      if (numberRegex.test(message)) {
        // console.log ("⚠️  Fallback number match found:", q.key);
        return q.key;
      }
    }

    // console.log ("❌ No matching question found in message");
    return null;
  };

  const loadDocxPreview = async () => {
  if (!questionnaireFileId) {
    showToastMessage("No document available for preview");
    return;
  }

  try {
    setIsLoadingDocx(true);
    setProcessingMessage("Loading document preview...");

    const payload = {
      onedrive_file_id: questionnaireFileId,
      surveyFileName: surveyFileName,
      powner: powner
    };

    const response = await fetch(`${config.API_BASE_URL}/get_embed_url`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error("Failed to get embed URL");
    }

    const data = await response.json();

    if (data.success) {
      setEmbedUrl(data.embed_url);
    } else {
      showToastMessage("Failed to load document preview");
      setShowSplitView(false);
    }

  } catch (error) {
    console.error("Error previewing document:", error);
    showToastMessage("Error loading document preview");
    setShowSplitView(false);
  } finally {
    setIsLoadingDocx(false);
  }
};

const handleCommandResponse = (data) => {
  setAiMessages((prev) => [
    ...prev,
    { role: "assistant", content: data.response },
  ]);

  if (data.session) {
    updateProgressUI(data.session);
  }

  switch (data.commandType) {
    case "show_changes":
      if (data.commandData) {
        setCompareData(data.commandData);
        setShowCompareModal(true);
      }
      break;
      
    case "reset_question":
      if (data.modifiedJson) {
        setSdataGroup((prev) => ({
          ...prev,
          [data.targetQuestionKey]: data.modifiedJson,
        }));
        setHasUnsavedChanges(true);
      }
      break;
      
    case "show_pending":
      // Could show a list of pending changes
      if (data.commandData?.pending?.length > 0) {
        // Handle pending list display
        console.log("Pending changes:", data.commandData.pending);
      }
      break;
      
    // ... other cases
  }
};
// Update the AI message rendering to include confirmation buttons
const renderAIMessage = (msg, index) => {
  if (msg.component === "confirmation_buttons" && msg.pendingChange) {
    return (
      <div key={index} style={{
        display: "flex",
        gap: "10px",
        justifyContent: "center",
        marginTop: "10px",
        marginBottom: "10px",
      }}>
        <button
          onClick={() => confirmPendingChange(msg.pendingChange.questionKey, msg.pendingChange.changeId, 'accept')}
          style={{
            padding: "8px 16px",
            backgroundColor: "#10b981",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          ✓ Accept Change
        </button>
        <button
          onClick={() => confirmPendingChange(msg.pendingChange.questionKey, msg.pendingChange.changeId, 'reject')}
          style={{
            padding: "8px 16px",
            backgroundColor: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          ✗ Reject Change
        </button>
        <button
          onClick={() => {
            setCompareData({
              original: sdataGroup[msg.pendingChange.questionKey],
              current: msg.pendingChange.proposed,
              question_key: msg.pendingChange.questionKey
            });
            setShowCompareModal(true);
          }}
          style={{
            padding: "8px 16px",
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          Compare
        </button>
      </div>
    );
  }
  // Regular message rendering
  return (
    <div key={index} style={{
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "12px 16px",
      marginBottom: "8px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      border: "1px solid #dadce0",
    }}>
      {/* Your existing message rendering */}
    </div>
  );
};

// AI Assistant Functions
  const initializeAISession = async () => {
    try {
      // Extract question numbers
      const qNumbers = extractQuestionNumbers(sdataGroup);
      setQuestionNumbers(qNumbers);

      const response = await fetch(`${config.API_BASE_URL}/api/ai/initialize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          questionNumbers: qNumbers.map((q) => q.displayName),
        }),
      });

      const data = await response.json();

      if (data.success) {
        setAiMessages([
          {
            role: "assistant",
            content: data.message,
          },
        ]);
      }
    } catch (error) {
      console.error("Error initializing AI session:", error);
      setAiMessages([
        {
          role: "assistant",
          content: "⚠️ Failed to initialize AI assistant. Please try again.",
        },
      ]);
    }
  };

// Update sendMessageToAI to handle pending changes
const sendMessageToAI = async (userMessage) => {
  setIsAiThinking(true);

  const newUserMessage = { role: "user", content: userMessage };
  setAiMessages((prev) => [...prev, newUserMessage]);

  try {
    const targetKey = targetQuestionKey || findTargetQuestion(userMessage);

    const targetQuestion = targetKey ? sdataGroup[targetKey] : {};

    const response = await fetch(`${config.API_BASE_URL}/api/ai/chatnew`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        message: userMessage,
        targetQuestionKey: targetKey,
        targetQuestionData: targetQuestion,
        surveyFileName: surveyFileName,
        questionnaireFileId: questionnaireFileId,
        fullQuestionnaire: sdataGroup,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get response from AI");
    }

    const data = await response.json();

    // Handle command responses
    if (data.isCommand) {
      handleCommandResponse(data);
      return;
    }

    // Handle pending change (needs confirmation)
    if (data.pendingChange) {
      // Store pending change
      setPendingChanges(prev => ({
        ...prev,
        [data.pendingChange.questionKey]: data.pendingChange
      }));

      // Show confirmation UI in chat
      setAiMessages((prev) => [
        ...prev,
        { 
          role: "assistant", 
          content: data.response,
          pendingChange: data.pendingChange // Attach to message
        },
      ]);

      // Add confirmation buttons
      setAiMessages((prev) => [
        ...prev,
        {
          role: "system",
          content: "pending_confirmation",
          pendingChange: data.pendingChange,
          component: "confirmation_buttons"
        },
      ]);
    }
    // Handle accepted change
    else if (data.action === 'change_accepted' && data.modifiedJson) {
      // Apply the change
      setSdataGroup((prev) => ({
        ...prev,
        [data.targetQuestionKey]: data.modifiedJson,
      }));
      setHasUnsavedChanges(true);
      
      // Clear pending change
      setPendingChanges(prev => {
        const newPending = {...prev};
        delete newPending[data.targetQuestionKey];
        return newPending;
      });

      setAiMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response },
      ]);

      if (onShowToastMessage) {
        onShowToastMessage(`✅ Change applied to question ${data.targetQuestionKey}`);
      }
    }
    // Handle rejected change
    else if (data.action === 'change_rejected' && data.originalJson) {
      // Revert to original
      setSdataGroup((prev) => ({
        ...prev,
        [data.targetQuestionKey]: data.originalJson,
      }));
      
      // Clear pending change
      setPendingChanges(prev => {
        const newPending = {...prev};
        delete newPending[data.targetQuestionKey];
        return newPending;
      });

      setAiMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response },
      ]);
    }
    // Handle show changes comparison
    else if (data.commandType === 'show_changes' && data.commandData) {
      setCompareData(data.commandData);
      setShowCompareModal(true);
    }
    // Regular response
    else if (data.hasModification && data.modifiedJson) {
      // Direct modification (old behavior - kept for backward compatibility)
      setSdataGroup((prev) => ({
        ...prev,
        [targetKey]: data.modifiedJson,
      }));
      setHasUnsavedChanges(true);

      setAiMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response },
      ]);
    } else {
      setAiMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response },
      ]);
    }

    if (data.session) {
      updateProgressUI(data.session);
    }

  } catch (error) {
    console.error("AI API Error:", error);
    setAiMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: `❌ Error: ${error.message}`,
      },
    ]);
  } finally {
    setIsAiThinking(false);
  }
};

// New function to update progress UI
const updateProgressUI = (sessionData) => {
  // You can add a progress bar or indicator in your UI
  console.log("Progress update:", sessionData.progress);
  
  // Optionally show a small progress indicator in the AI panel
  const progressElement = document.getElementById('ai-progress-indicator');
  if (progressElement) {
    progressElement.innerHTML = `Progress: ${sessionData.reviewedCount}/${sessionData.totalCount} (${sessionData.progress.completion_percentage.toFixed(0)}%)`;
  }
};
// New function to confirm or reject a pending change
const confirmPendingChange = async (questionKey, changeId, action) => {
  setIsAiThinking(true);

  try {
    const response = await fetch(`${config.API_BASE_URL}/api/ai/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        message: action === 'accept' ? 'accept change' : 'reject change',
        targetQuestionKey: questionKey,
        targetQuestionData: sdataGroup[questionKey],
        surveyFileName: surveyFileName,
        questionnaireFileId: questionnaireFileId,
        fullQuestionnaire: sdataGroup,
        confirmAction: action,
        changeId: changeId
      }),
    });

    const data = await response.json();

    if (data.action === 'change_accepted' && data.modifiedJson) {
      setSdataGroup((prev) => ({
        ...prev,
        [questionKey]: data.modifiedJson,
      }));
      setHasUnsavedChanges(true);
      
      setAiMessages((prev) => [
        ...prev,
        { role: "assistant", content: `✅ Change accepted and applied!` },
      ]);
    } else if (data.action === 'change_rejected') {
      setAiMessages((prev) => [
        ...prev,
        { role: "assistant", content: `🔄 Change rejected. Question unchanged.` },
      ]);
    }

    // Clear pending change
    setPendingChanges(prev => {
      const newPending = {...prev};
      delete newPending[questionKey];
      return newPending;
    });

    if (data.session) {
      updateProgressUI(data.session);
    }

  } catch (error) {
    console.error("Confirmation error:", error);
    setAiMessages((prev) => [
      ...prev,
      { role: "assistant", content: `❌ Error processing confirmation: ${error.message}` },
    ]);
  } finally {
    setIsAiThinking(false);
  }
};

// Add comparison modal component
const ComparisonModal = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 10001 },
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          transform: "translate(-50%, -50%)",
          width: "800px",
          maxWidth: "90%",
          maxHeight: "80%",
          padding: "20px",
        },
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3>Compare Changes - Question {data.question_key}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer" }}>×</button>
      </div>
      
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        <div>
          <h4 style={{ color: "#dc2626", marginBottom: "10px" }}>Original</h4>
          <pre style={{ 
            backgroundColor: "#fee2e2", 
            padding: "15px", 
            borderRadius: "8px",
            overflow: "auto",
            maxHeight: "400px",
            fontSize: "12px"
          }}>
            {JSON.stringify(data.original, null, 2)}
          </pre>
        </div>
        <div>
          <h4 style={{ color: "#059669", marginBottom: "10px" }}>Current / Proposed</h4>
          <pre style={{ 
            backgroundColor: "#d1fae5", 
            padding: "15px", 
            borderRadius: "8px",
            overflow: "auto",
            maxHeight: "400px",
            fontSize: "12px"
          }}>
            {JSON.stringify(data.current, null, 2)}
          </pre>
        </div>
      </div>
      
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
        <button
          onClick={onClose}
          style={{
            padding: "8px 16px",
            backgroundColor: "#6b7280",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};


  const handleSendMessage = () => {
    if (!userInput.trim()) return;
    sendMessageToAI(userInput);
    setUserInput("");
  };

  const resetAISession = () => {
    setAiMessages([]);
    initializeAISession();
  };

  const handlePreviewChange = (value) => {
    if (value.startsWith("export_")) {
      const exportType = value.replace("export_", "");
      setSelectedExport(exportType);
      setPreviewType("export");
    } else {
      setSelectedExport(null);
      setPreviewType(value);
    }
  };

  const renderExportPreview = () => {
    if (!selectedExport || !exportedFiles[selectedExport]) {
      return (
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            color: "#6b7280",
          }}
        >
          <p>No export selected</p>
          <p style={{ fontSize: "14px", marginTop: "10px" }}>
            Select an export from the dropdown above to view its content
          </p>
        </div>
      );
    }

    const exportData = exportedFiles[selectedExport];

    return (
      <div className="export-preview-container">
        <div
          style={{
            padding: "12px 20px",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: "14px", color: "#374151" }}>
              {exportData.fileName}
            </h4>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "12px",
                color: "#6b7280",
              }}
            >
              Exported: {new Date(exportData.timestamp).toLocaleString()}
            </p>
          </div>
          <button
            onClick={() => {
              const blob = new Blob([exportData.content], {
                type: "text/plain",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = exportData.fileName;
              a.click();
              URL.revokeObjectURL(url);
            }}
            style={{
              backgroundColor: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            Download
          </button>
        </div>
        <div
          style={{
            padding: "20px",
            overflowY: "auto",
            height: "calc(100% - 80px)",
            backgroundColor: "#ffffff",
          }}
        >
          <pre
            style={{
              margin: 0,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: "12px",
              lineHeight: "1.5",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              color: "#1f2937",
            }}
          >
            {exportData.content}
          </pre>
        </div>
      </div>
    );
  };
const badgeStyle = (bg, color) => ({
    fontSize: "0.75rem",
    padding: "3px 8px",
    backgroundColor: bg,
    color,
    borderRadius: "4px",
    fontWeight: 600,
    display: "inline-block",
  });
  const renderRowBadges = (row) => {
    if (!row) return null;
    const badges = [];

    if (row.screenout) badges.push(<span key="so" style={badgeStyle("#fee2e2", "#991b1b")}>Screen Out</span>);
    if (row.exclusive) badges.push(<span key="ex" style={badgeStyle("#dbeafe", "#1e40af")}>Exclusive</span>);
    if (row.other)     badges.push(<span key="ot" style={badgeStyle("#fef3c7", "#92400e")}>Other (specify)</span>);
    if (row.fix)       badges.push(<span key="fx" style={badgeStyle("#e0e7ff", "#3730a3")}>Fixed</span>);
    if (row.quotaCounts) badges.push(<span key="qt" style={badgeStyle("#f3e8ff", "#6b21a8")}>Quota: {row.quotaCounts}</span>);
    if (row.punchLogic)  badges.push(<span key="pl" style={badgeStyle("#ecfdf5", "#065f46")}>Punch: {row.punchLogic}</span>);
    if (row.filterLogic) badges.push(<span key="fl" style={badgeStyle("#f3e8ff", "#6b21a8")}>Filter: {row.filterLogic}</span>);

    return badges.length > 0 ? (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>{badges}</div>
    ) : null;
  };
  // ─────────────────────────────────────────────────────────────
  // Respondent View (Survey)
  // ─────────────────────────────────────────────────────────────
  const renderSurveyView = (q) => {
    if (!q) return <div style={{ color: "#6b7280", padding: "2rem" }}>No question data</div>;

    const titleHtml = { __html: q.Qtitle || "No question text" };
    const titleinst = { __html: q.instructions || "No question text" };
    const type = q.type || "Unknown";

    if (type === "Intro") {
      return (
        <div style={{ textAlign: "center", padding: "2.5rem 1rem" }}>
          <div style={{ fontSize: "1.25rem", marginBottom: "2rem" }} dangerouslySetInnerHTML={titleHtml} />
          <button style={{ padding: "12px 40px", background: "#2563eb", color: "white", border: "none", borderRadius: "6px", fontSize: "1rem" }}>
            CONTINUE
          </button>
        </div>
      );
    }

    if (type === "Single Response" && q.rows?.length > 0) {
      return <SingleResponsePreview question={q} />;
    }

    if (type === "Numeric") {
      const oe = q.oeparameters || {};
      return (
        <div>
          <div style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1.6rem" }} dangerouslySetInnerHTML={titleHtml} />
          <input
            type="number"
            min={oe.min}
            max={oe.max}
            placeholder="Enter your answer"
            readOnly
            style={{ width: "200px", padding: "10px 14px", fontSize: "1rem", border: "1px solid #d1d5db", borderRadius: "6px" }}
          />
        </div>
      );
    }

    if (q.rows?.length > 0 && q.columns?.length > 0) {
      const isMulti = type.includes("Multi") || type === "Multi Grid";
      return (
        <div>
          <div style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1.6rem" }} dangerouslySetInnerHTML={titleHtml} />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", border: "1px solid #d1d5db", minWidth: "400px", fontWeight: 600, fontSize: "0.95rem" }}>
                    Statement / Item
                  </th>
                  {q.columns.map((col) => (
                    <th
                      key={col.index}
                      style={{ padding: "12px 14px", textAlign: "center", border: "1px solid #d1d5db", minWidth: "130px", fontWeight: 600, fontSize: "0.9rem", whiteSpace: "normal" }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: col.text || "—" }} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.rows.map((row, i) => (
                  <tr key={row.index || i} style={{ background: i % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
                    <td style={{ padding: "12px 16px", border: "1px solid #d1d5db", fontSize: "0.98rem" }}>
                      <div style={{ whiteSpace: "normal", wordBreak: "break-word", marginBottom: "6px" }} dangerouslySetInnerHTML={{ __html: row.text || "—" }} />
                      {renderRowBadges(row)}
                    </td>
                    {q.columns.map((col) => (
                      <td key={col.index} style={{ textAlign: "center", padding: "12px", border: "1px solid #d1d5db" }}>
                        <input type={isMulti ? "checkbox" : "radio"} readOnly style={{ width: "18px", height: "18px" }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div style={{ color: "#6b7280", padding: "2rem 0" }}>
        <div style={{ fontSize: "1.25rem" }} dangerouslySetInnerHTML={titleHtml} />
        <p style={{ marginTop: "1rem" }}>(No preview for type: {type})</p>
      </div>
    );
  };
  const toggleSplitView = () => {
    if (
      !questionnaireFileId ||
      !surveyFileName?.toLowerCase().endsWith(".docx")
    ) {
      showToastMessage("DOCX preview is not available");
      return;
    }
    setShowSplitView(!showSplitView);
  };

  // Handle draggable divider for DOCX split view
  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newRatio =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setSplitRatio(Math.max(20, Math.min(80, newRatio)));
    },
    [isDragging]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // Handle draggable divider for AI panel
  const handleAiMouseDown = (e) => {
    setIsAiDragging(true);
    e.preventDefault();
  };

  const handleAiMouseMove = useCallback(
    (e) => {
      if (!isAiDragging || !aiContainerRef.current) return;
      const containerRect = aiContainerRef.current.getBoundingClientRect();
      const newRatio =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setAiSplitRatio(Math.max(30, Math.min(70, newRatio)));
    },
    [isAiDragging]
  );

  const handleSaveSurvey = async () => {
    if (!projectId) {
      showToastMessage("❌ No project ID available");
      return;
    }

    try {
      setIsSaving(true);
      setProcessingMessage("Saving survey...");

      const response = await fetch(`${config.API_BASE_URL}/save-survey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          projectId,
          sdataGroup,
          client: JSON.stringify(selectedClient),
          project: JSON.stringify(selectedProject),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save survey");
      }

      const result = await response.json();
      showToastMessage("✅ Survey saved successfully!");
      setHasUnsavedChanges(false);
      if (onSurveyUpdate) {
        onSurveyUpdate(sdataGroup);
      }
      setProcessingMessage("");
    } catch (error) {
      console.error("Error saving survey:", error);
      showToastMessage("❌ Failed to save survey");
    } finally {
      setIsSaving(false);
      setProcessingMessage("");
    }
  };

  const handleAiMouseUp = () => {
    setIsAiDragging(false);
  };

  useEffect(() => {
    if (isAiDragging) {
      document.addEventListener("mousemove", handleAiMouseMove);
      document.addEventListener("mouseup", handleAiMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleAiMouseMove);
        document.removeEventListener("mouseup", handleAiMouseUp);
      };
    }
  }, [isAiDragging, handleAiMouseMove]);

  const speak = (text) => {
    if ("speechSynthesis" in window && !isSpeaking) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.2;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
    }
  };

  const handleVoiceCommand = (command) => {
    if (isSpeaking) return;
    // console.log ("Processing command:", command);
    let updated = false;
    const qMatch =
      command.match(/question (\d+(?:\.\d+)?)/i) ||
      command.match(/q(\d+(?:\.\d+)?)/i);
    const questionNum = qMatch ? parseInt(qMatch[1]) : null;
    const questionKey = questionNum
      ? questionNum.toString()
      : Object.keys(sdataGroup)[0];

    if (!sdataGroup || !sdataGroup[questionKey]) {
      speak(`Question not found.`);
      return;
    }

    const isBold = /bold|emphasize|strong/i.test(command);
    const isUnbold = /unbold|remove bold|normal/i.test(command);
    const targetTitle = /title|question text/i.test(command);
    const targetRow = command.match(/(?:row|option)s?\s*(\d+(?:\.\d+)?)/i);
    const targetCol = command.match(/(?:column|col)s?\s*(\d+(?:\.\d+)?)/i);
    const targetStmt = command.match(/(?:statement|item)s?\s*(\d+(?:\.\d+)?)/i);

    let target = null;
    let index = null;
    let subKey = null;

    if (targetTitle) {
      target = "Qtitle";
    } else if (targetRow) {
      const rowNum = parseInt(targetRow[1]) - 1;
      if (
        sdataGroup[questionKey]?.rows &&
        rowNum >= 0 &&
        rowNum < sdataGroup[questionKey].rows.length
      ) {
        target = "rows";
        index = rowNum;
        subKey = "text";
      } else {
        speak(`Row ${targetRow[1]} not found.`);
        return;
      }
    } else if (targetCol) {
      const colNum = parseInt(targetCol[1]) - 1;
      if (
        sdataGroup[questionKey]?.columns &&
        colNum >= 0 &&
        colNum < sdataGroup[questionKey].columns.length
      ) {
        target = "columns";
        index = colNum;
        subKey = "text";
      } else {
        speak(`Column ${targetCol[1]} not found.`);
        return;
      }
    } else if (targetStmt) {
      const stmtNum = parseInt(targetStmt[1]) - 1;
      if (
        sdataGroup[questionKey]?.statements &&
        stmtNum >= 0 &&
        stmtNum < sdataGroup[questionKey].statements.length
      ) {
        target = "statements";
        index = stmtNum;
        subKey = "text";
      } else {
        speak(`Statement ${targetStmt[1]} not found.`);
        return;
      }
    }

    if (target && (isBold || isUnbold)) {
      if (isBold) {
        updated = updateTextBold(questionKey, target, index, subKey);
        const what = target === "Qtitle" ? "title" : `${target} ${index + 1}`;
        speak(`${what} bolded.`);
      } else if (isUnbold) {
        updated = updateTextUnbold(questionKey, target, index, subKey);
        const what = target === "Qtitle" ? "title" : `${target} ${index + 1}`;
        speak(`${what} unbolded.`);
      }
    }

    if (!updated && command.length > 5 && !/stop|exit|cancel/i.test(command)) {
      setTimeout(() => {
        speak("Command not recognized. Try 'bold title' or 'unbold row two'.");
      }, 300);
    }

    if (updated) {
      setSdataGroup((prev) => ({ ...prev }));
    }
  };

  const updateTextBold = (questionKey, target, index = null, subKey = null) => {
    const toggleBold = (text) => {
      if (typeof text !== "string") return text;
      const boldRegex = /^<strong>\s*(.*?)\s*<\/strong>$/i;
      if (boldRegex.test(text)) {
        return text.replace(boldRegex, "$1").trim();
      }
      return `<strong>${text}</strong>`;
    };

    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const question = newData[questionKey];

      if (target === "Qtitle") {
        question.Qtitle = toggleBold(question.Qtitle || "");
      } else if (index !== null && question[target]?.[index] && subKey) {
        question[target][index][subKey] = toggleBold(
          question[target][index][subKey]
        );
      }
      return newData;
    });
    return true;
  };

  const updateTextUnbold = (
    questionKey,
    target,
    index = null,
    subKey = null
  ) => {
    const stripBold = (text) => {
      if (typeof text !== "string") return text;
      return text.replace(/<strong>\s*(.*?)\s*<\/strong>/gi, "$1");
    };

    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const question = newData[questionKey];

      if (target === "Qtitle") {
        question.Qtitle = stripBold(question.Qtitle || "");
      } else if (index !== null && question[target]?.[index] && subKey) {
        question[target][index][subKey] = stripBold(
          question[target][index][subKey]
        );
      }
      return newData;
    });
    return true;
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      speak("Voice not supported.");
      return;
    }
    speechSynthesis.cancel();
    setIsSpeaking(false);
    if (isListening) {
      recognitionRef.current.stop();
      setTranscript("");
    } else {
      setTranscript("Ready...");
      recognitionRef.current.start();
    }
    setIsListening(!isListening);
    if (!isSpeaking) {
      speak(isListening ? "Stopped." : "Say your command now.");
    }
  };

  const getRowSettingLabels = (row) => {
    const labels = [];

    if (row.screenout) {
      labels.push({ text: "Screen Out", color: "#dc2626", bgColor: "#fecaca" });
    }

    if (row.other) {
      labels.push({ text: "Other", color: "#7c3aed", bgColor: "#ddd6fe" });
    }

    if (row.otherPiping) {
      labels.push({
        text: `Piping ${row.otherPiping}`,
        color: "#0369a1",
        bgColor: "#bae6fd",
      });
    }

    if (row.filterLogic) {
      labels.push({
        text: `Logic: ${row.filterLogic}`,
        color: "#ea580c",
        bgColor: "#fed7aa",
      });
    }

    if (row.randomize) {
      labels.push({ text: "Randomize", color: "#059669", bgColor: "#a7f3d0" });
    }

    if (row.exclusive) {
      labels.push({ text: "Exclusive", color: "#d97706", bgColor: "#fde68a" });
    }

    if (row.hidden) {
      labels.push({ text: "Hidden", color: "#4b5563", bgColor: "#d1d5db" });
    }

    if (row.anchor) {
      labels.push({ text: "Anchor", color: "#65a30d", bgColor: "#d9f99d" });
    }

    if (row.rotationGroup) {
      labels.push({
        text: `Rotation: ${row.rotationGroup}`,
        color: "#be185d",
        bgColor: "#fbcfe8",
      });
    }

    if (row.fix) {
      labels.push({ text: "Fix", color: "#4338ca", bgColor: "#c7d2fe" });
    }

    if (row.header) {
      labels.push({ text: "Header", color: "#0f766e", bgColor: "#99f6e4" });
    }

    if (row.settings) {
      Object.entries(row.settings).forEach(([key, value]) => {
        if (
          value !== false &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          labels.push({
            text: `${key}: ${value}`,
            color: "#6b7280",
            bgColor: "#e5e7eb",
          });
        }
      });
    }

    return labels;
  };

  const renderQuestionsPreview = () => {
    if (!sdataGroup || Object.keys(sdataGroup).length === 0) {
      return <div className="preview-empty">No questions available</div>;
    }

    const sortedEntries = Object.entries(sdataGroup).sort((a, b) => {
      const orderA = a[1]._order || 0;
      const orderB = b[1]._order || 0;
      return orderA - orderB;
    });

    return sortedEntries.map(([questionNumber, question]) => (
      <div key={questionNumber} className="preview-question">
        <div className="preview-question-header">
          <span className="preview-qnumber">
            {question.Question_number || questionNumber}
          </span>
          <span className="preview-qtype">{question.type}</span>
        </div>

        <div
          className="preview-qtitle"
          dangerouslySetInnerHTML={{ __html: question.Qtitle || "No title" }}
        />

        {question.instructions && (
          <div className="preview-instructions">
            <strong>Instructions:</strong>
            <span dangerouslySetInnerHTML={{ __html: question.instructions }} />
          </div>
        )}

        {question.logics && (
          <div className="preview-logic">
            <strong>Logic:</strong> {question.logics}
          </div>
        )}

        {question.rows && question.rows.length > 0 && (
          <div className="preview-rows">
            <strong>Rows ({question.rows.length}):</strong>
            <ul>
              {question.rows.map((row, index) => {
                const settingLabels = getRowSettingLabels(row);
                return (
                  <li key={index} className="preview-row-item">
                    <div className="row-content">
                      <span className="row-index">
                        {row.index || index + 1}
                      </span>
                      <div className="row-text-and-settings">
                        <span
                          className="row-text"
                          dangerouslySetInnerHTML={{ __html: row.text }}
                        />
                        {settingLabels.length > 0 && (
                          <div className="row-settings-inline">
                            {settingLabels.map((label, labelIndex) => (
                              <span
                                key={labelIndex}
                                className="row-setting-inline"
                                style={{
                                  color: label.color,
                                  backgroundColor: label.bgColor,
                                  borderColor: label.color,
                                }}
                              >
                                {label.text}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {question.columns && question.columns.length > 0 && (
          <div className="preview-columns">
            <strong>Columns ({question.columns.length}):</strong>
            <div className="preview-columns-list">
              {question.columns.map((col, index) => {
                const settingLabels = getRowSettingLabels(col);
                return (
                  <div key={index} className="preview-column-item">
                    <span className="column-index">
                      {col.index || index + 1}.
                    </span>
                    <div className="column-text-and-settings">
                      <span
                        className="column-text"
                        dangerouslySetInnerHTML={{ __html: col.text }}
                      />
                      {settingLabels.length > 0 && (
                        <div className="column-settings-inline">
                          {settingLabels.map((label, labelIndex) => (
                            <span
                              key={labelIndex}
                              className="column-setting-inline"
                              style={{
                                color: label.color,
                                backgroundColor: label.bgColor,
                                borderColor: label.color,
                              }}
                            >
                              {label.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {question.statements && question.statements.length > 0 && (
          <div className="preview-statements">
            <strong>Statements ({question.statements.length}):</strong>
            <ul>
              {question.statements.map((stmt, index) => (
                <li
                  key={index}
                  dangerouslySetInnerHTML={{ __html: stmt.text }}
                />
              ))}
            </ul>
          </div>
        )}

        <div className="preview-settings">
          {question.rowsRandomisation && (
            <span className="setting-badge">Rows Random</span>
          )}
          {question.columnsRandomisation && (
            <span className="setting-badge">Cols Random</span>
          )}
          {question.rowsRotation && (
            <span className="setting-badge">Rows Rotate</span>
          )}
          {question.columnsRotation && (
            <span className="setting-badge">Cols Rotate</span>
          )}
          {question.alphabeticalOrder && (
            <span className="setting-badge">A-Z Order</span>
          )}
          {question.columnIndex && (
            <span className="setting-badge">Col Index</span>
          )}
        </div>

        {question.oeparameters &&
          Object.entries(question.oeparameters).filter(
            ([, value]) =>
              value !== false &&
              value !== null &&
              value !== undefined &&
              value !== ""
          ).length > 0 && (
            <div className="preview-section">
              <label>Parameters:</label>
              <div className="preview-oe-params">
                {Object.entries(question.oeparameters)
                  .filter(
                    ([, value]) =>
                      value !== false &&
                      value !== null &&
                      value !== undefined &&
                      value !== ""
                  )
                  .map(([key, value]) => (
                    <div key={key} className="oe-param-item">
                      <span className="param-key">{key}:</span>
                      <span className="param-value">{String(value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
      </div>
    ));
  };

  const renderJSONPreview = () => {
    return (
      <div className="preview-json">
        <pre>{JSON.stringify(sdataGroup, null, 2)}</pre>
      </div>
    );
  };

  const renderStatisticsPreview = () => {
    if (!sdataGroup) return <div>No data available</div>;

    const totalQuestions = Object.keys(sdataGroup).length;
    const questionTypes = {};
    let totalRows = 0;
    let totalColumns = 0;
    let totalStatements = 0;
    let rowsWithSettings = 0;
    let columnsWithSettings = 0;

    Object.values(sdataGroup).forEach((question) => {
      questionTypes[question.type] = (questionTypes[question.type] || 0) + 1;
      totalRows += (question.rows || []).length;
      totalColumns += (question.columns || []).length;
      totalStatements += (question.statements || []).length;

      if (question.rows) {
        question.rows.forEach((row) => {
          if (getRowSettingLabels(row).length > 0) {
            rowsWithSettings++;
          }
        });
      }

      if (question.columns) {
        question.columns.forEach((column) => {
          if (getRowSettingLabels(column).length > 0) {
            columnsWithSettings++;
          }
        });
      }
    });

    return (
      <div className="preview-stats">
        <h4>Survey Statistics</h4>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-value">{totalQuestions}</span>
            <span className="stat-label">Total Questions</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalRows}</span>
            <span className="stat-label">Total Rows</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalColumns}</span>
            <span className="stat-label">Total Columns</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{totalStatements}</span>
            <span className="stat-label">Total Statements</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{rowsWithSettings}</span>
            <span className="stat-label">Rows with Settings</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{columnsWithSettings}</span>
            <span className="stat-label">Columns with Settings</span>
          </div>
        </div>

        <div className="question-types">
          <h5>Question Types</h5>
          {Object.entries(questionTypes).map(([type, count]) => (
            <div key={type} className="type-item">
              <span className="type-name">{type}</span>
              <span className="type-count">{count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Preview Button */}

<CommonButton label="Preview" title="Preview Survey" onClick={() => setIsPreviewOpen(true)} />

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onRequestClose={() => setIsPreviewOpen(false)}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            height: "95%",
            maxWidth: "1600px",
            borderRadius: "12px",
            padding: "0",
            overflow: "hidden",
          },
        }}
      >
        <div className="preview-modal">

          <div className="preview-header">
            <h2>

              Survey Preview -{" "}
              {surveyName || surveyFileName || "Untitled Survey"}
            </h2>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {/* Preview Type Selector */}
              <div className="preview-tabs">
                <select
                  value={
                    previewType === "export"
                      ? `export_${selectedExport}`
                      : previewType
                  }
                  onChange={(e) => handlePreviewChange(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    outline: "none",
                    minWidth: "200px",
                  }}
                >
                  <optgroup label="Survey Views">
                    <option value="questions">Questions</option>
                    <option value="statistics">Statistics</option>
                    <option value="json">Raw JSON</option>
                    <option value="survey">Survey</option>
                  </optgroup>
                  {Object.keys(exportedFiles).length > 0 && (
                    <optgroup label="Exported Files">
                      {Object.entries(exportedFiles).map(([type, data]) => (
                        <option key={type} value={`export_${type}`}>
                          {type.toUpperCase()} ({data.extension}) -{" "}
                          {new Date(data.timestamp).toLocaleDateString()}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              {/* DOCX Split View Button */}
              {questionnaireFileId &&
                surveyFileName?.toLowerCase().endsWith(".docx") && (
                  <button
                    onClick={toggleSplitView}
                    disabled={isLoadingDocx}
                    style={{
                      backgroundColor: showSplitView ? "#10b981" : "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      cursor: isLoadingDocx ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    {isLoadingDocx
                      ? "Loading..."
                      : showSplitView
                      ? "✓ Split View"
                      : "📄 Show DOCX"}
                  </button>
                )}

              {/* AI Assistant Button */}
              {hasDownloadFeatureAccess("ai_mode") && (
                <button
                  onClick={() => {
                    setShowAIPanel(!showAIPanel);
                    if (!showAIPanel) {
                      initializeAISession();
                    }
                  }}
                  style={{
                    backgroundColor: showAIPanel ? "#10b981" : "#8b5cf6",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span></span>
                  {showAIPanel ? "✓ AI Mode" : "AI Mode"}
                </button>
              )}

              <button
                onClick={handleSaveSurvey}
                disabled={isSaving || !hasUnsavedChanges}
                style={{
                  backgroundColor: hasUnsavedChanges ? "#f59e0b" : "#9ca3af",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    isSaving || !hasUnsavedChanges ? "not-allowed" : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: isSaving || !hasUnsavedChanges ? 0.6 : 1,
                }}
              >
                {isSaving
                  ? "⏳ Saving..."
                  : hasUnsavedChanges
                  ? "Save Changes"
                  : "✓ Saved"}
              </button>

              {/* Close Button */}
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="close-button"
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ×
              </button>

              {/* Voice Button */}
              <button
                onClick={toggleListening}
                className={`voice-button ${isListening ? "active" : ""}`}
                title="Voice Commands (e.g., 'bold title in question 1')"
                style={{
                  backgroundColor: isListening ? "#ef4444" : "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
              >
                {isListening ? "🔴" : "🎤"}
              </button>
              {transcript && (
                <div style={{ fontSize: "12px", color: "gray" }}>
                  Last: {transcript}
                </div>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div
            ref={containerRef}
            className="preview-content-wrapper"
            style={{
              display: "flex",
              height: showAIPanel ? "calc(100% - 130px)" : "calc(100% - 70px)",
              position: "relative",
            }}
          >
            {/* Survey Preview */}
            <div
              className="preview-content"
              style={{
                width: showSplitView ? `${splitRatio}%` : "100%",
                overflowY: "auto",
                padding: "20px",
                transition: isDragging ? "none" : "width 0.3s ease",
              }}
            >
              {previewType === "questions" && renderQuestionsPreview()}
              {previewType === "statistics" && renderStatisticsPreview()}
              {previewType === "json" && renderJSONPreview()}
              {previewType === "export" && renderExportPreview()}
              {previewType === "survey" && (<>{renderSurveyView(currentQuestion)}<div
              style={{
                padding: "12px 24px",
                borderTop: "1px solid #e2e8f0",
                background: "#f8fafc",
                display: "flex",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <button
                onClick={handleBack}
                disabled={isFirst}
                style={{
                  padding: "10px 28px",
                  background: isFirst ? "#e2e8f0" : "#64748b",
                  color: isFirst ? "#94a3b8" : "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: isFirst ? "not-allowed" : "pointer",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                ← Back
              </button>

              <button
                onClick={handleNext}
                disabled={isLast}
                style={{
                  padding: "10px 36px",
                  background: isLast ? "#10b981" : "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: isLast ? "default" : "pointer",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                {isLast ? "End of Survey" : "Next →"}
              </button>
            </div></>)}
            
{showCompareModal && (
  <ComparisonModal
    isOpen={showCompareModal}
    onClose={() => setShowCompareModal(false)}
    data={compareData}
  />
)}


{Object.keys(pendingChanges).length > 0 && (
  <div style={{
    backgroundColor: "#fef3c7",
    border: "1px solid #fbbf24",
    borderRadius: "8px",
    padding: "8px 12px",
    marginBottom: "12px",
    fontSize: "12px",
    color: "#92400e",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }}>
    <span>⏳</span>
    <span>You have {Object.keys(pendingChanges).length} pending change(s) waiting for confirmation</span>
  </div>
)}
            </div>

            {/* DOCX Divider */}
            {showSplitView && (
              <div
                onMouseDown={handleMouseDown}
                style={{
                  width: "5px",
                  cursor: "col-resize",
                  backgroundColor: "#e5e7eb",
                  position: "relative",
                  flexShrink: 0,
                  transition: isDragging ? "none" : "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  if (!isDragging) {
                    e.currentTarget.style.backgroundColor = "#e5e7eb";
                  }
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "40px",
                    backgroundColor: isDragging ? "#3b82f6" : "#9ca3af",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px",
                  }}
                >
                  ⋮
                </div>
              </div>
            )}

            {/* DOCX Preview Panel */}
            {showSplitView && (
              <div
                className="docx-preview-panel"
                style={{
                  width: `${100 - splitRatio}%`,
                  backgroundColor: "#f9fafb",
                  display: "flex",
                  flexDirection: "column",
                  transition: isDragging ? "none" : "width 0.3s ease",
                  position: "relative",
                }}
              >
                {isDragging && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 9999,
                      cursor: "col-resize",
                    }}
                  />
                )}

                <div style={{ flex: 1, overflow: "hidden" }}>
                  {embedUrl ? (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Document Preview"
                      style={{ border: "none" }}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: "#6b7280",
                      }}
                    >
                      {isLoadingDocx
                        ? "Loading document..."
                        : "No preview available"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* AI Prompt Bar */}
          {showAIPanel && (
            <div
              style={{
                position: "absolute",
                bottom: 20,
                left: 0,
                right: 0,
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              {/* Recent Messages */}
              {visibleMessages.length > 0 && (
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {visibleMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: "12px 16px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                        border: "1px solid #dadce0",
                        animation: "slideIn 0.3s ease-out",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <span style={{ fontSize: "16px", flexShrink: 0 }}>
                          {msg.role === "user"
                            ? "👤"
                            : msg.role === "system"
                            ? ""
                            : ""}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#5f6368",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {msg.role === "user"
                              ? "You"
                              : msg.role === "system"
                              ? "Status"
                              : "AI Assistant"}
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color:
                                msg.role === "system" ? "#166534" : "#202124",
                              lineHeight: "1.5",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {msg.content.length > 200
                              ? msg.content.substring(0, 200) + "..."
                              : msg.content}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Input Bar */}
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "50px",
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  border: "1px solid #dadce0",
                }}
              >
                <button
                  style={{
                    width: "44px",
                    height: "44px",
                    backgroundColor: "#8b5cf6",
                    border: "none",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    cursor: "default",
                  }}
                >
                  <span style={{ fontSize: "20px" }}></span>
                </button>

                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && !isAiThinking && handleSendMessage()
                  }
                  placeholder="Ask EraAI to modify your survey"
                  disabled={isAiThinking}
                  style={{
                    flex: 1,
                    backgroundColor: "transparent",
                    border: "none",
                    outline: "none",
                    color: "#202124",
                    fontSize: "16px",
                    padding: "10px 0",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                  }}
                />

                <button
                  onClick={resetAISession}
                  style={{
                    width: "44px",
                    height: "44px",
                    backgroundColor: "transparent",
                    border: "none",
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    flexShrink: 0,
                    color: "#5f6368",
                  }}
                  title="Reset conversation"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f1f3f4";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  🔄
                </button>

                <button
                  onClick={handleSendMessage}
                  disabled={isAiThinking || !userInput.trim()}
                  style={{
                    width: "44px",
                    height: "44px",
                    backgroundColor:
                      isAiThinking || !userInput.trim() ? "#f1f3f4" : "#8b5cf6",
                    border: "none",
                    borderRadius: "50%",
                    cursor:
                      isAiThinking || !userInput.trim()
                        ? "not-allowed"
                        : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    flexShrink: 0,
                    color:
                      isAiThinking || !userInput.trim() ? "#80868b" : "white",
                  }}
                  onMouseEnter={(e) => {
                    if (!isAiThinking && userInput.trim()) {
                      e.currentTarget.style.backgroundColor = "#7c3aed";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isAiThinking && userInput.trim()) {
                      e.currentTarget.style.backgroundColor = "#8b5cf6";
                    }
                  }}
                >
                  {isAiThinking ? (
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        border: "2px solid #5f6368",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                  ) : (
                    "➤"
                  )}
                </button>

                <button
                  onClick={() => setShowAIPanel(false)}
                  style={{
                    width: "44px",
                    height: "44px",
                    backgroundColor: "transparent",
                    border: "none",
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    flexShrink: 0,
                    color: "#5f6368",
                  }}
                  title="Close AI Assistant"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f1f3f4";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .preview-modal {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }

        .preview-header h2 {
          margin: 0;
          color: #333;
          font-size: 18px;
        }

        .preview-tabs {
          display: flex;
          gap: 4px;
        }

        .preview-tabs button {
          padding: 8px 16px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 14px;
          color: #6b7280;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .preview-tabs button:hover {
          color: #3b82f6;
        }

        .preview-tabs button.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
        }

        .preview-row-item {
          margin-bottom: 4px;
          padding: 4px 6px;
        }

        .row-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .row-index {
          font-weight: bold;
          min-width: 20px;
          color: #6b7280;
          font-size: 13px;
        }

        .row-text-and-settings {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .row-text {
          font-size: 13px;
        }

        .row-settings-inline {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .row-setting-inline {
          font-size: 10px;
          padding: 1px 5px;
          border-radius: 8px;
          border: 1px solid;
          font-weight: 500;
          white-space: nowrap;
        }

        .preview-column-item {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 2px;
          padding: 2px 4px;
        }

        .column-index {
          font-weight: bold;
          min-width: 16px;
          color: #6b7280;
          font-size: 11px;
        }

        .column-text-and-settings {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }

        .column-text {
          font-size: 11px;
        }

        .column-settings-inline {
          display: flex;
          gap: 2px;
          flex-wrap: wrap;
        }

        .column-setting-inline {
          font-size: 9px;
          padding: 1px 3px;
          border-radius: 6px;
          border: 1px solid;
          font-weight: 500;
          white-space: nowrap;
        }

        .preview-rows ul {
          list-style: none;
          padding: 0;
          margin: 4px 0;
        }

        .preview-rows li {
          border-left: 2px solid #e5e7eb;
          padding-left: 8px;
        }

        .preview-columns-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .export-preview-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          background-color: #ffffff;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
      `}</style>

    </>
  );
};

export default SurveyPreview;
